import css from "./Dashboard.sass?inline"
import {Lifecycle, RuntimeSignal, StringComparator, TimeSpan} from "@opendaw/lib-std"
import {Await, createElement, DomElement} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService.ts"
import {Html} from "@opendaw/lib-dom"
import {Icon} from "@/ui/components/Icon"
import {IconSymbol} from "@opendaw/studio-enums"
import {Dialogs} from "@/ui/components/dialogs"
import {ProjectSignals, ProjectStorage} from "@opendaw/studio-core"
import {ThreeDots} from "@/ui/spinner/ThreeDots"

function createNoteSvg(): SVGSVGElement {
    const ns = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(ns, 'svg')
    svg.setAttribute('viewBox', '-2 -38 24 44')
    const head = document.createElementNS(ns, 'ellipse')
    head.setAttribute('cx', '6'); head.setAttribute('cy', '0'); head.setAttribute('rx', '6.2'); head.setAttribute('ry', '4.7')
    head.setAttribute('transform', 'rotate(-22 6 0)'); head.setAttribute('fill', 'var(--acc)')
    const stem = document.createElementNS(ns, 'path')
    stem.setAttribute('d', 'M 11.4 -3 L 13.6 -32'); stem.setAttribute('stroke', 'var(--acc)'); stem.setAttribute('stroke-width', '1.8'); stem.setAttribute('stroke-linecap', 'round'); stem.setAttribute('fill', 'none')
    const flag = document.createElementNS(ns, 'path')
    flag.setAttribute('d', 'M 13.6 -32 Q 20 -27, 17 -20'); flag.setAttribute('stroke', 'var(--acc)'); flag.setAttribute('stroke-width', '1.8'); flag.setAttribute('stroke-linecap', 'round'); flag.setAttribute('fill', 'none')
    svg.append(head, stem, flag)
    return svg
}

function createNoteSpan(): HTMLSpanElement {
    const wrapper = document.createElement('span')
    wrapper.style.cssText = 'display:inline-block;width:0.34em;height:1em;vertical-align:baseline;position:relative;margin-left:0.02em'
    const svg = createNoteSvg()
    svg.style.cssText = 'position:absolute;bottom:0.04em;left:0;width:100%;height:auto;overflow:visible;filter:drop-shadow(0 0 6px var(--acc));opacity:0.98'
    wrapper.appendChild(svg)
    return wrapper
}

const className = Html.adoptStyleSheet(css, "Dashboard")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

export const Dashboard = ({lifecycle, service}: Construct) => {
    const now = new Date().getTime()
    const heroNote = createNoteSpan()
    const cardNote = createNoteSvg()
    cardNote.setAttribute('width', '12'); cardNote.setAttribute('height', '20')
    cardNote.style.cssText = 'overflow:visible;filter:drop-shadow(0 0 5px var(--acc));opacity:0.98'
    return (
        <div className={className}>
            <div className="home">
                <h1 className="hero">What are we<br/>making today{heroNote}</h1>
                <div className="start-grid">
                    <div className="card card--accent" onclick={() => service.newProject()}>
                        <div className="card-icon"><span>+</span></div>
                        <div className="card-body">
                            <div className="card-title">Blank session</div>
                            <div className="card-desc">Empty timeline. Bring stems when you're ready.</div>
                        </div>
                        <span className="card-arrow">→</span>
                    </div>
                    <div className="card">
                        <div className="card-icon"><span>{cardNote}</span></div>
                        <div className="card-body">
                            <div className="card-title">From reference</div>
                            <div className="card-desc">Drop a song. Sonn will set up a chain that aims at it.</div>
                        </div>
                        <span className="card-arrow">→</span>
                    </div>
                    <div className="card">
                        <div className="card-icon"><span>↑</span></div>
                        <div className="card-body">
                            <div className="card-title">Import stems</div>
                            <div className="card-desc">WAVs, MP3s, a folder. Sonn auto-organises and balances.</div>
                        </div>
                        <span className="card-arrow">→</span>
                    </div>
                    <div className="card">
                        <div className="card-icon"><span>⊞</span></div>
                        <div className="card-body">
                            <div className="card-title">From a template</div>
                            <div className="card-desc">Singer-songwriter · electronic · podcast · live takes.</div>
                        </div>
                        <span className="card-arrow">→</span>
                    </div>
                </div>
                <div className="recent-section">
                    <div className="recent-label">RECENT</div>
                    <div className="project-table">
                        <div className="table-head">
                            <div className="col-name">NAME</div>
                            <div className="col-modified">LAST EDITED</div>
                            <div className="col-tracks">TRACKS</div>
                            <div className="col-tempo">TEMPO · KEY</div>
                            <div className="col-actions"/>
                        </div>
                        <Await
                            factory={() => ProjectStorage.listProjects()}
                            loading={() => (<div className="table-loading">{ThreeDots()}</div>)}
                            failure={({reason, retry}) => (
                                <div className="table-error" onclick={retry}>
                                    {reason instanceof DOMException ? reason.name : String(reason)}
                                </div>
                            )}
                            repeat={exec => lifecycle.own(RuntimeSignal.subscribe(
                                signal => signal === ProjectSignals.StorageUpdated && exec()))}
                            success={projects => {
                                const sorted = projects.toSorted(
                                    (a, b) => -StringComparator(a.meta.modified, b.meta.modified))
                                return (
                                    <div className="table-body">
                                        {sorted.length === 0 && (
                                            <div className="table-empty">No projects yet. Start with a blank session.</div>
                                        )}
                                        {sorted.map(({uuid, meta}) => {
                                            const timeString = TimeSpan.millis(
                                                new Date(meta.modified).getTime() - now).toUnitString()
                                            const icon: DomElement = (
                                                <Icon symbol={IconSymbol.Delete} className="delete-icon"/>
                                            )
                                            const row: HTMLElement = (
                                                <div className="table-row"
                                                     onclick={() => service.projectProfileService.load(uuid, meta)}>
                                                    <div className="col-name">
                                                        <div className="waveform-thumb"/>
                                                        <div className="project-info">
                                                            <span className="project-name">{meta.name}</span>
                                                            {meta.artist.length > 0 && (
                                                                <span className="project-artist">{meta.artist}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-modified">{timeString}</div>
                                                    <div className="col-tracks">—</div>
                                                    <div className="col-tempo">—</div>
                                                    <div className="col-actions">{icon}</div>
                                                </div>
                                            )
                                            icon.onclick = event => {
                                                event.stopPropagation()
                                                Dialogs.approve({
                                                    headline: "Delete Project?",
                                                    message: "Are you sure? This cannot be undone."
                                                }).then(approved => {
                                                    if (approved) {
                                                        service.deleteProject(uuid, meta).then(() => row.remove())
                                                    }
                                                })
                                            }
                                            return row
                                        })}
                                    </div>
                                )
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}